import { useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { Modal } from "@/components/ui/Modal";
import { useApproveRequisition, useRequisition } from "../hooks/useRequisitions";
import { MANUAL_ORDER_TYPE_OPTIONS } from "../types";
import { RequisitionAutoLineItems, toggleableAutoRows } from "./RequisitionAutoLineItems";

export interface RequisitionApproveTarget {
  id: string;
  deadline: string | null;
  suggestedOrderType: string | null;
}

/**
 * „Zatwierdź zapotrzebowanie" z wiersza listy — termin, typ zamówienia i pozycje automatyczne
 * umowy (pełne okno z kamieniami milowymi i plikami ZE jest na stronie zapotrzebowania).
 * Wspólne dla listy zapotrzebowań i zbiorczego „Do zatwierdzenia" dyrektora, więc obie
 * tabele wysyłają te same nadpisania.
 */
export function RequisitionApproveModal({
  target,
  onClose,
  onApproved,
}: {
  target: RequisitionApproveTarget | null;
  onClose: () => void;
  onApproved?: () => void;
}) {
  const approveMutation = useApproveRequisition();
  const [deadline, setDeadline] = useState("");
  const [orderType, setOrderType] = useState("standard");
  // Wiersz listy nie niesie pozycji automatycznych (auto_line_items tylko w szczególe) —
  // okno zatwierdzania dociąga szczegół, żeby pokazać, co automat dopisze do zamówienia.
  const { data: detail, isLoading: detailLoading } = useRequisition(target?.id ?? "");
  const [declinedAuto, setDeclinedAuto] = useState<Set<string>>(new Set());
  const autoRows = detail?.id === target?.id ? detail?.auto_line_items : null;

  useEffect(() => {
    setDeadline(target?.deadline ?? "");
    setOrderType(target?.suggestedOrderType ?? "standard");
    approveMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.id]);

  useEffect(() => {
    if (detail && detail.id === target?.id) {
      setDeclinedAuto(new Set(detail.declined_auto_line_item_types ?? []));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.id, target?.id]);

  if (!target) return null;

  const handleConfirm = () => {
    // Mirror the detail page: carry the WPW order_type override so a requisition raised on the
    // Działki tab is not approved as STANDARD (which would skip WpwOwnerHandover materialization).
    const overrides: Record<string, unknown> = {};
    if (deadline) overrides.deadline = deadline;
    if (orderType && orderType !== "standard") overrides.order_type = orderType;
    if (toggleableAutoRows(autoRows).length > 0) {
      overrides.declined_auto_line_item_types = [...declinedAuto];
    }
    approveMutation.mutate(
      { id: target.id, overrides: Object.keys(overrides).length ? overrides : undefined },
      {
        onSuccess: () => {
          onApproved?.();
          onClose();
        },
      },
    );
  };

  return (
    <Modal open title="Zatwierdź zapotrzebowanie" onClose={onClose} size="sm">
      <div className="flex flex-col gap-4">
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Podaj termin realizacji zamówienia. Jest to wymagane do zatwierdzenia zapotrzebowania.
        </p>
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
            Termin realizacji *
          </label>
          <DatePicker
            value={deadline}
            onChange={setDeadline}
            min={new Date().toISOString().split("T")[0]}
          />
        </div>
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
            Typ zamówienia
          </label>
          {target.suggestedOrderType ? (
            // Raised from the DLC Działki tab → locked to the WPW type chosen there.
            <>
              <input
                disabled
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 cursor-not-allowed"
                value={
                  target.suggestedOrderType === "wpw_terenowe"
                    ? "Pozyskanie zgód WPW (terenowe)"
                    : "Pozyskanie zgód WPW (kompleksowe, art.124)"
                }
              />
              <p className="text-xs text-gray-500 mt-1">Ustawione automatycznie — zapotrzebowanie z trybu „Działki / zgody prywatne”.</p>
            </>
          ) : (
            <select
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              value={orderType}
              onChange={(e) => setOrderType(e.target.value)}
            >
              {MANUAL_ORDER_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          )}
        </div>
        <RequisitionAutoLineItems
          rows={autoRows}
          declined={declinedAuto}
          onToggle={(autoId) =>
            setDeclinedAuto((s) => {
              const next = new Set(s);
              if (next.has(autoId)) next.delete(autoId);
              else next.add(autoId);
              return next;
            })
          }
        />
        {approveMutation.isError && (
          <p className="text-sm text-red-600">
            {(approveMutation.error as { response?: { data?: { errors?: { detail?: string } } } })
              ?.response?.data?.errors?.detail ?? "Błąd zatwierdzenia. Spróbuj ponownie."}
          </p>
        )}
        <div className="flex gap-3">
          <Button
            loading={approveMutation.isPending}
            // Najpierw podgląd pozycji automatycznych — zatwierdzenie „na ślepo” to właśnie
            // dopisana bez wiedzy zatwierdzającego premia.
            disabled={!deadline || detailLoading}
            onClick={handleConfirm}
          >
            Zatwierdź
          </Button>
          <Button variant="secondary" onClick={onClose}>Anuluj</Button>
        </div>
      </div>
    </Modal>
  );
}
